import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronDownIcon, 
  CogIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

interface ConfigCardProps {
  category: {
    id: string;
    name: string;
    description: string;
    icon: React.ComponentType<{ className?: string }>;
    configs: any;
    hasOverrides: boolean;
    overrideCount: number;
    status: 'default' | 'customized' | 'warning';
  };
  isExpanded: boolean;
  onToggle: () => void;
  onConfigureClick?: () => void;
  children?: React.ReactNode;
}

export const ConfigCard: React.FC<ConfigCardProps> = ({
  category,
  isExpanded,
  onToggle,
  onConfigureClick,
  children
}) => {
  const IconComponent = category.icon || CogIcon;

  const getStatusInfo = () => {
    switch (category.status) {
      case 'customized':
        return {
          icon: CheckCircleIcon,
          color: 'text-green-400',
          bgColor: 'bg-green-400/10',
          borderColor: 'border-green-400/30',
          label: `${category.overrideCount} customized`
        };
      case 'warning':
        return {
          icon: ExclamationTriangleIcon,
          color: 'text-yellow-400',
          bgColor: 'bg-yellow-400/10',
          borderColor: 'border-yellow-400/30',
          label: 'Needs attention'
        };
      default:
        return {
          icon: ClockIcon,
          color: 'text-blue-100',
          bgColor: 'bg-blue-400/10',
          borderColor: 'border-blue-400/30',
          label: 'Using defaults'
        };
    }
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  return (
    <motion.div
      layout
      className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300"
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      {/* Card Header */}
      <div 
        onClick={onToggle}
        className="p-6 cursor-pointer hover:bg-white/5 transition-colors duration-200 relative"
      >
        {/* Status & Toggle - Top Right Corner */}
        <div className="absolute top-4 right-4 flex items-center space-x-3 flex-shrink-0">
          {/* Status Badge */}
          <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusInfo.bgColor} ${statusInfo.borderColor} border whitespace-nowrap`}>
            <StatusIcon className={`w-3 h-3 mr-1.5 ${statusInfo.color}`} />
            <span className={statusInfo.color}>{statusInfo.label}</span>
          </div>

          {/* Expand/Collapse Icon */}
          {isExpanded ? (
            <motion.button
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
              className="p-1 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <XMarkIcon className="w-5 h-5 text-white/60 hover:text-white/80" />
            </motion.button>
          ) : (
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDownIcon className="w-5 h-5 text-white/60" />
            </motion.div>
          )}
        </div>

        {/* Icon and Title Row */}
        <div className="flex items-center space-x-4 mb-3 pr-32">
          {/* Category Icon */}
          <div className="flex-shrink-0">
            <div className={`${isExpanded ? 'w-16 h-16' : 'w-12 h-12'} bg-white/10 rounded-lg flex items-center justify-center transition-all duration-300`}>
              <IconComponent className={`${isExpanded ? 'w-8 h-8' : 'w-6 h-6'} text-white/80 transition-all duration-300`} />
            </div>
          </div>
          
          {/* Title - Now has full space without interference */}
          <div className="flex-1 min-w-0">
            <h3 className={`${isExpanded ? 'text-2xl' : 'text-lg'} font-semibold text-white/90 transition-all duration-300`}>
              {category.name}
            </h3>
            {isExpanded && (
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-sm text-white/60 mt-1"
              >
                Configure {category.name.toLowerCase()} settings
              </motion.p>
            )}
          </div>
        </div>
        
        {/* Description - Full Width Below Icon and Title */}
        <div>
          <p className="text-sm text-white/70 leading-relaxed break-words whitespace-pre-wrap">
            {category.description}
          </p>
        </div>

        {/* Quick Action Button */}
        {!isExpanded && (
          <div className="mt-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onConfigureClick?.();
              }}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white/80 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 hover:border-white/30 transition-all duration-200"
            >
              <CogIcon className="w-4 h-4 mr-2" />
              Configure
            </button>
          </div>
        )}
      </div>

      {/* Expandable Content */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            <div className="px-6 pb-6 border-t border-white/10">
              <motion.div 
                className="pt-4"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.3 }}
              >
                {children}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
